// useTableEditState.js

import { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from 'context/UserContext';

import { coerceFromInput, coerceToInput } from 'utilities/NodeFieldCoercion';
import {
    buildUrlFieldValueFromInput,
    getUrlString,
} from 'utilities/UrlFieldValue';
import {
    buildNodeSubtitlePatch,
    getNodeSubtitle,
    isSubtitleFieldKey,
    isSubtitleFieldType,
} from 'utilities/NodeSubtitle';

import { updateNode } from 'services/nodes/NodeWriteService';
import {
    getDataSectionName,
    getFieldType,
    isInlineEditable,
    resolveFieldValue,
} from 'views/table/utils/tableFieldUtils';

const useTableEditState = ({ typeDefKey, fieldDataSection }) => {
    const { user } = useUser();
    const [editingCell, setEditingCell] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const editingCellRef = useRef(null);

    useEffect(() => {
        editingCellRef.current = editingCell;
    }, [editingCell]);

    const getFieldValueForEdit = useCallback((node, fieldDef, fieldKey) => {
        if (!node) return '';
        const isSubtitleField = isSubtitleFieldKey(fieldKey, fieldDef?.type)
            || isSubtitleFieldType(fieldDef?.type);
        if (fieldKey === 'title') return coerceToInput(node?.title, 'string');
        if (isSubtitleField) return coerceToInput(getNodeSubtitle(node), 'string');
        const rawValue = resolveFieldValue(node, fieldDef, fieldDataSection);
        const fieldType = getFieldType(fieldDef, fieldKey);
        if (fieldType === 'url') return getUrlString(rawValue);
        return coerceToInput(rawValue, fieldType, fieldDef);
    }, [fieldDataSection]);

    const handleStartEdit = useCallback((node, fieldDef, fieldKey) => {
        if (!node?.key) return;
        if (!isInlineEditable(fieldDef, fieldKey)) return;
        setEditingCell({ nodeKey: node.key, fieldKey });
        setEditingValue(getFieldValueForEdit(node, fieldDef, fieldKey));
    }, [getFieldValueForEdit]);

    const clearEditingCellIfCurrent = useCallback((expected) => {
        const current = editingCellRef.current;
        if (!expected || (current
            && expected.nodeKey === current.nodeKey
            && expected.fieldKey === current.fieldKey)) {
            setEditingCell(null);
            setEditingValue('');
        }
    }, []);

    const handleCancelEdit = useCallback(() => {
        clearEditingCellIfCurrent(editingCellRef.current);
    }, [clearEditingCellIfCurrent]);

    const handleCommitEdit = useCallback(async (node, fieldDef, fieldKey) => {
        if (!node?.key) return;
        if (!isInlineEditable(fieldDef, fieldKey)) return;

        const expectedCell = { nodeKey: node.key, fieldKey };

        const fieldType = getFieldType(fieldDef, fieldKey);
        const priorRaw = fieldType === 'url'
            ? resolveFieldValue(node, fieldDef, fieldDataSection)
            : null;
        const nextValue = fieldType === 'url'
            ? buildUrlFieldValueFromInput(editingValue, priorRaw)
            : coerceFromInput(editingValue, fieldType, fieldDef);
        try {
            if (fieldKey === 'title') {
                const title = String(nextValue ?? '').trim();
                await updateNode(node.key, { title }, { actor: user });
                clearEditingCellIfCurrent(expectedCell);
                return;
            }

            const isSubtitleField = isSubtitleFieldKey(fieldKey, fieldDef?.type)
                || isSubtitleFieldType(fieldDef?.type);

            if (isSubtitleField) {
                await updateNode(node.key, buildNodeSubtitlePatch(nextValue), { actor: user });
                clearEditingCellIfCurrent(expectedCell);
                return;
            }

            const dataKey = getDataSectionName(typeDefKey);
            if (!dataKey) return;
            const fieldName = fieldDef?.name;
            if (!fieldName) return;
            const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
                ? node[dataKey]
                : {};

            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: nextValue,
                },
            }, { actor: user });

            clearEditingCellIfCurrent(expectedCell);
        } catch (error) {
            console.error('[TableView] update cell error:', error);
        }
    }, [clearEditingCellIfCurrent, editingValue, fieldDataSection, typeDefKey, user]);

    return {
        editingCell,
        editingValue,
        setEditingValue,
        handleStartEdit,
        handleCancelEdit,
        handleCommitEdit,
    };
};

export default useTableEditState;
